// src/hooks/useIsPhone.js
//
// "I'm not sure if showing the whole map is going to be realistic on
// mobile… maybe you're only having to select a room and then you're able
// to see the cards." (operator, 2026-09-04)
//
// A phone is not a small desktop: the racks surfaces render genuinely
// different views below this width, so the switch is made in JS and not
// only in CSS. CSS alone can hide a plan grid; it cannot stop it being
// built, measured and made draggable.
import { useEffect, useState } from 'react';

/**
 * "Phone" is TWO conditions, not one width (review S9).
 *
 * A phone in landscape is 844-932 CSS px wide, so a width-only 768px
 * switch handed a rotated phone the desktop floor plan — complete with
 * drag grips that a finger cannot use, because dnd-kit is mounted with a
 * PointerSensor and no TouchSensor on purpose. The second clause catches
 * exactly that case: a coarse pointer up to tablet width. A mouse-driven
 * 900px window is untouched and still gets the map.
 *
 * A comma is an OR in a media query list, and matchMedia evaluates the
 * whole list — so this stays one query and one listener.
 */
export const PHONE_QUERY =
  '(max-width: 768px), (pointer: coarse) and (max-width: 1024px)';

/**
 * KNOWN LIMITATION, not fixed here: crossing the boundary swaps whole
 * component trees, so an open editor's unsaved form is lost on rotation.
 * The modals are the app's own <Modal>s and live outside both layouts'
 * views, but the board itself remounts its children. Fixing it means
 * hoisting the editors' state above the switch, which is a bigger change
 * than a rotation edge case earns today.
 */

/**
 * Reads a media query safely anywhere: server render, jsdom without a
 * matchMedia polyfill, and old Safari (which has addListener but not
 * addEventListener on the MediaQueryList).
 */
const readMatch = (query) => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return !!window.matchMedia(query).matches;
  } catch (err) {
    return false;
  }
};

export default function useIsPhone(query = PHONE_QUERY) {
  // The initial value is read synchronously, so the first paint is
  // already the right layout — a phone must never flash the desktop map.
  const [isPhone, setIsPhone] = useState(() => readMatch(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    let list;
    try {
      list = window.matchMedia(query);
    } catch (err) {
      return undefined;
    }
    // ⚠️ A matchMedia that ANSWERS NOTHING is not the same as one that
    // THROWS, and only the throw was guarded. `readMatch` above catches
    // its own, so the initial value survives — but this effect went on
    // to read `.matches` off whatever came back. A jest mock whose
    // implementation CRA's `resetMocks: true` has wiped returns
    // `undefined`, and the hook then took the whole tree down with a
    // TypeError the moment a new caller mounted alongside it. Same
    // shape as a polyfill that answers null.
    if (!list) return undefined;
    const onChange = (event) => setIsPhone(!!event.matches);
    // Re-read on mount: the query may have changed between the initial
    // state and this effect (a rotation during hydration).
    setIsPhone(!!list.matches);

    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    }
    if (typeof list.addListener === 'function') {   // Safari < 14
      list.addListener(onChange);
      return () => list.removeListener(onChange);
    }
    return undefined;
  }, [query]);

  return isPhone;
}
